import { useEffect, useRef, useState } from "react";
import Sidebar from "../components/layout/Sidebar";
import { NavLink as Link, useNavigate } from "react-router-dom";
import {
  authRequestNbGet,
  sendPutRequest,
  sendFileRequest,
  assetUrl,
  refreshToken,
} from "../utils/helpers";
import Loader from "../components/common/Loader";
import swal from "sweetalert";

const DEFAULT_IMG =
  "https://cdn-icons-png.flaticon.com/512/4140/4140048.png";

const EMPTY_FORM = {
  id: "",
  name: "",
  email: "",
  mobile: "",
  wallet_address: "",
};

const EMPTY_ERRORS = {
  id: "",
  name: "",
  email: "",
  mobile: "",
  wallet_address: "",
};

const ProfilePage = () => {
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mainTravelerId, setMainTravelerId] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);

  const [errors, setErrors] = useState(EMPTY_ERRORS);

  const [formData, setFormData] = useState(EMPTY_FORM);

  const [profileImg, setProfileImg] = useState(DEFAULT_IMG);

  const savedProfileRef = useRef(EMPTY_FORM);
  const savedProfileImgRef = useRef(DEFAULT_IMG);
  const fetchedRef = useRef(false);

  const savedUser = sessionStorage.getItem("user");

  let user = null;

  if (savedUser && savedUser !== "undefined") {
    try {
      user = JSON.parse(savedUser);
    } catch (error) {
      user = null;
    }
  }

  const getApiMessage = (
    response,
    fallback = "Something went wrong. Please try again."
  ) => {
    if (!response) {
      return fallback;
    }

    if (response.message) {
      return response.message;
    }

    if (response.data?.message) {
      return response.data.message;
    }

    if (response.error) {
      return response.error;
    }

    const validationErrors =
      response.errors ||
      response.data?.errors;

    if (
      validationErrors &&
      typeof validationErrors === "object"
    ) {
      const firstError = Object.values(
        validationErrors
      )[0];

      if (Array.isArray(firstError)) {
        return firstError[0] || fallback;
      }

      if (typeof firstError === "string") {
        return firstError;
      }
    }

    return fallback;
  };

  const logoutUser = async (
    message = "Your session has expired. Please login again."
  ) => {
    await swal({
      title: "Session Expired",
      text: message,
      icon: "warning",
      button: "Login",
    });

    sessionStorage.removeItem("user");
    sessionStorage.removeItem("isLogin");
    localStorage.removeItem("token");

    navigate("/login", {
      replace: true,
    });
  };

  const getProfileImageSrc = (image) => {
    if (!image) {
      return DEFAULT_IMG;
    }

    const value = String(image).trim();

    if (
      value.startsWith("data:") ||
      value.startsWith("blob:") ||
      /^https?:\/\//i.test(value)
    ) {
      return value;
    }

    const cleanPath = value.replace(/^\/+/, "");

    if (cleanPath.startsWith("storage/")) {
      return `${assetUrl}/${cleanPath}`;
    }

    return `${assetUrl}/storage/${cleanPath}`;
  };

  const validateName = (value) => {
    const name = String(value || "").trim();

    if (!name) {
      return "Name is required";
    }

    if (name.length < 2) {
      return "Name must be at least 2 characters";
    }

    if (name.length > 50) {
      return "Name must not exceed 50 characters";
    }

    if (!/^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/.test(name)) {
      return "Name can contain letters only";
    }

    return "";
  };

  const validateProfileEmail = (value) => {
    const email = String(value || "").trim();

    if (!email) {
      return "Email address is required";
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      return "Enter a valid email address";
    }

    return "";
  };

  const validateMobile = (value) => {
    const mobile = String(value || "").trim();

    if (!mobile) {
      return "Mobile number is required";
    }

    if (!/^\d+$/.test(mobile)) {
      return "Mobile number can contain digits only";
    }

    if (mobile.length !== 10) {
      return "Mobile number must be 10 digits";
    }

    if (!/^[6-9]\d{9}$/.test(mobile)) {
      return "Enter a valid mobile number";
    }

    return "";
  };

  useEffect(() => {
    if (!user) {
      return;
    }

    if (fetchedRef.current) {
      return;
    }

    fetchedRef.current = true;

    const loadProfile = async () => {
      try {
        setPageLoading(true);

        const response =
          await authRequestNbGet(
            "me/get-profile"
          );

        if (response?.status === 200) {
          if (response.token) {
            await refreshToken(
              response.token
            );
          }

          const profile =
            response.data || {};

          const profileData = {
            id: profile.id || "",
            name: profile.name || "",
            mobile:
              profile.mobile || "",
            email:
              profile.email || "",
            wallet_address:
              profile.wallet_address ||
              "",
          };

          setMainTravelerId(
            profile.id || null
          );

          setFormData(profileData);

          savedProfileRef.current = {
            ...profileData,
          };

          if (profile.profile_image) {
            setProfileImg(
              profile.profile_image
            );

            savedProfileImgRef.current =
              profile.profile_image;
          }

          return;
        }

        if (
          response?.status === 2 &&
          !response?.token
        ) {
          await logoutUser(
            getApiMessage(
              response,
              "Your session has expired. Please login again."
            )
          );

          return;
        }

        await swal({
          title: "Unable to Load Profile",
          text: getApiMessage(
            response,
            "We could not load your profile. Existing profile information has been preserved."
          ),
          icon: "error",
          button: "OK",
        });
      } catch (error) {
        console.error(
          "Profile load error:",
          error
        );

        await swal({
          title: "Unable to Load Profile",
          text:
            error?.message ||
            "A network or server error occurred. Existing profile information has been preserved.",
          icon: "error",
          button: "OK",
        });
      } finally {
        setPageLoading(false);
      }
    };

    loadProfile();
  }, [navigate]);

  const handleImageChange = async (e) => {
    const file =
      e.target.files?.[0];

    if (!file) {
      return;
    }

    const allowedTypes = [
      "image/png",
      "image/jpeg",
    ];

    if (
      !allowedTypes.includes(file.type)
    ) {
      await swal({
        title: "Invalid Image",
        text: "Please select a PNG, JPG, or JPEG image.",
        icon: "error",
        button: "OK",
      });

      e.target.value = "";
      return;
    }

    const previousImage =
      savedProfileImgRef.current;

    try {
      const previewImage =
        await new Promise(
          (resolve, reject) => {
            const reader =
              new FileReader();

            reader.onload = () => {
              resolve(reader.result);
            };

            reader.onerror = () => {
              reject(
                new Error(
                  "Unable to read selected image."
                )
              );
            };

            reader.readAsDataURL(file);
          }
        );

      setProfileImg(
        previewImage
      );

      setPageLoading(true);

      const payload = {
        profile_image: file,
      };

      const response =
        await sendFileRequest(
          payload,
          "/me/update-profile-image"
        );

      if (response?.status === 200) {
        if (response.token) {
          await refreshToken(
            response.token
          );
        }

        const updatedImage =
          response?.data
            ?.profile_image;

        if (updatedImage) {
          setProfileImg(
            updatedImage
          );

          savedProfileImgRef.current =
            updatedImage;
        } else {
          savedProfileImgRef.current =
            previewImage;
        }

        await swal({
          title: "Profile Image Updated",
          text: getApiMessage(
            response,
            "Your profile image has been updated successfully."
          ),
          icon: "success",
          button: "OK",
        });

        return;
      }

      setProfileImg(
        previousImage
      );

      if (
        response?.status === 2 &&
        !response?.token
      ) {
        await logoutUser(
          getApiMessage(
            response,
            "Your session has expired. Please login again."
          )
        );

        return;
      }

      await swal({
        title: "Upload Failed",
        text: getApiMessage(
          response,
          "Profile image could not be updated. Your previous image has been restored."
        ),
        icon: "error",
        button: "OK",
      });
    } catch (error) {
      console.error(
        "Profile image upload error:",
        error
      );

      setProfileImg(
        previousImage
      );

      await swal({
        title: "Upload Failed",
        text:
          error?.message ||
          "Profile image could not be updated. Your previous image has been restored.",
        icon: "error",
        button: "OK",
      });
    } finally {
      setPageLoading(false);

      if (e.target) {
        e.target.value = "";
      }
    }
  };

  const saveProfile = async () => {
    const name = String(
      formData.name || ""
    ).trim();

    const email = String(
      formData.email || ""
    ).trim();

    const mobile = String(
      formData.mobile || ""
    ).trim();

    const newErrors = {
      ...EMPTY_ERRORS,
      name: validateName(name),
      email: validateProfileEmail(email),
      mobile: validateMobile(mobile),
    };

    const hasError =
      Object.values(
        newErrors
      ).some(Boolean);

    if (hasError) {
      setErrors(newErrors);

      await swal({
        title: "Please Check Your Details",
        text: "Please correct the highlighted fields before saving your profile.",
        icon: "error",
        button: "OK",
      });

      return;
    }

    if (!mainTravelerId) {
      await swal({
        title: "Profile Error",
        text: "Traveler information is unavailable. Please refresh the page and try again.",
        icon: "error",
        button: "OK",
      });

      return;
    }

    try {
      setSaving(true);
      setPageLoading(true);

      const payload = {
        traveler_id:
          mainTravelerId,
        name,
        email,
        mobile,
      };

      const response =
        await sendPutRequest(
          payload,
          "me/profile"
        );

      if (
        response?.status === 200
      ) {
        if (response.token) {
          await refreshToken(
            response.token
          );
        }

        const responseProfile =
          response?.data || {};

        const updatedProfile = {
          id:
            responseProfile.id ??
            formData.id ??
            mainTravelerId,

          name:
            responseProfile.name ??
            name,

          email:
            responseProfile.email ??
            email,

          mobile:
            responseProfile.mobile ??
            mobile,

          wallet_address:
            responseProfile.wallet_address ??
            formData.wallet_address ??
            "",
        };

        setFormData(
          updatedProfile
        );

        savedProfileRef.current = {
          ...updatedProfile,
        };

        setErrors(
          EMPTY_ERRORS
        );

        setIsEditing(false);

        await swal({
          title: "Profile Updated",
          text: getApiMessage(
            response,
            "Your profile has been updated successfully."
          ),
          icon: "success",
          button: "OK",
        });

        return;
      }

      setFormData({
        ...savedProfileRef.current,
      });

      if (
        response?.status === 2 &&
        !response?.token
      ) {
        await logoutUser(
          getApiMessage(
            response,
            "Your session has expired. Please login again."
          )
        );

        return;
      }

      await swal({
        title: "Update Failed",
        text: getApiMessage(
          response,
          "Your profile could not be updated. Your previous saved values have been restored."
        ),
        icon: "error",
        button: "OK",
      });
    } catch (error) {
      console.error(
        "saveProfile error:",
        error
      );

      setFormData({
        ...savedProfileRef.current,
      });

      await swal({
        title: "Update Failed",
        text:
          error?.message ||
          "A server or network error occurred. Your previous saved values have been restored.",
        icon: "error",
        button: "OK",
      });
    } finally {
      setSaving(false);
      setPageLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;
    let errorMsg = "";

    if (name === "name") {
      if (newValue) {
        errorMsg = validateName(newValue);
      }
    }

    if (name === "email") {
      newValue = newValue.replace(/\s/g, "");

      if (newValue) {
        errorMsg = validateProfileEmail(newValue);
      }
    }

    if (name === "mobile") {
      newValue = newValue.replace(/\D/g, "");

      if (newValue.length > 10) {
        newValue = newValue.slice(0, 10);
      }

      if (newValue) {
        errorMsg = validateMobile(newValue);
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: errorMsg,
    }));
  };

  const handleNameBlur = () => {
    setErrors((prev) => ({
      ...prev,
      name: validateName(formData.name),
    }));
  };

  const handleEmailBlur = () => {
    setErrors((prev) => ({
      ...prev,
      email: validateProfileEmail(formData.email),
    }));
  };

  const handleMobileBlur = () => {
    setErrors((prev) => ({
      ...prev,
      mobile: validateMobile(formData.mobile),
    }));
  };

  return (
    <>
      <Loader
        isOpen={pageLoading}
        title={
          saving
            ? "Saving Profile"
            : "Loading Profile"
        }
        heading="Please Wait"
        message={
          saving
            ? "We are updating your profile details."
            : "We are loading your profile details."
        }
      />

      <div className="bgdashboard"></div>

      <Sidebar />

      <div className="dashboardMain sidebarPadding mainPanel">
        <div className="mainPanelIN">
          <div className="relative">

            <section className="documentsBanner">

              <div className="documentsBannerIcon">
                <div className="documentIcon">
                  <i className="fa-solid fa-user"></i>
                </div>
              </div>

              <div className="documentsBannerContent">
                <h3>
                  My Profile
                </h3>

                <p>
                  Manage your personal details for a seamless travel experience.
                </p>
              </div>

              <div className="documentsSkyline">
                <span className="skylineBuilding building1"></span>
                <span className="skylineBuilding building2"></span>
                <span className="skylineBuilding building3"></span>
                <span className="skylineBuilding building4"></span>

                <span className="skylineTower tower1"></span>
                <span className="skylineTower tower2"></span>
                <span className="skylineTower tower3"></span>

                <div className="travelBridge"></div>

                <i className="fa-solid fa-plane skylinePlane"></i>
              </div>

            </section>

            <div className="px-3 py-4 mb-4 rounded position-relative founderCard text-center">

              <h5 className="text-center mb-4">
                My Profile
              </h5>

              {/* <div className="d-flex justify-content-center mb-4 position-relative">

                <div
                  style={{
                    position:
                      "relative",
                  }}
                >
                  <img
                    src={getProfileImageSrc(
                      profileImg
                    )}
                    onError={(e) => {
                      e.currentTarget.onerror =
                        null;

                      e.currentTarget.src =
                        DEFAULT_IMG;
                    }}
                    alt="Profile Avatar"
                    style={{
                      width: "120px",
                      height: "120px",
                      borderRadius:
                        "50%",
                      objectFit:
                        "cover",
                      border:
                        "3px solid #f0f0f02b",
                    }}
                  />

                  {isEditing && (
                    <label
                      htmlFor="upload-profile"
                      className="upload-icon"
                      style={{
                        position:
                          "absolute",
                        bottom: "5px",
                        right: "5px",
                        background:
                          "#000",
                        color:
                          "#fff",
                        borderRadius:
                          "50%",
                        width: "32px",
                        height:
                          "32px",
                        display:
                          "flex",
                        justifyContent:
                          "center",
                        alignItems:
                          "center",
                        cursor:
                          "pointer",
                      }}
                    >
                      <i className="fa fa-camera"></i>
                    </label>
                  )}

                  <input
                    type="file"
                    id="upload-profile"
                    accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                    onChange={
                      handleImageChange
                    }
                    style={{
                      display:
                        "none",
                    }}
                  />
                </div>

              </div> */}

              <div className="d-flex justify-content-center mb-4 position-relative">
	<div
		style={{
			position: "relative",
			width: "120px",
			height: "120px",
		}}
	>
		<label
			htmlFor={isEditing ? "upload-profile" : undefined}
			style={{
				display: "block",
				width: "120px",
				height: "120px",
				borderRadius: "50%",
				cursor: isEditing ? "pointer" : "default",
				position: "relative",
			}}
		>
			<img
				src={getProfileImageSrc(profileImg)}
				onError={(e) => {
					e.currentTarget.onerror = null;
					e.currentTarget.src = DEFAULT_IMG;
				}}
				alt="Profile Avatar"
				style={{
					width: "120px",
					height: "120px",
					borderRadius: "50%",
					objectFit: "cover",
					border: "3px solid #f0f0f02b",
				}}
			/>

			{isEditing && (
				<div
					className="upload-icon"
					style={{
						position: "absolute",
						bottom: "5px",
						right: "5px",
						background: "#000",
						color: "#fff",
						borderRadius: "50%",
						width: "32px",
						height: "32px",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						cursor: "pointer",
					}}
				>
					<i className="fa fa-camera"></i>
				</div>
			)}
		</label>

		<input
			type="file"
			id="upload-profile"
			accept=".png,.jpg,.jpeg,image/png,image/jpeg"
			onChange={handleImageChange}
			style={{
				display: "none",
			}}
		/>
	</div>
</div>

              <div className="row justify-content-center">

                <div className="col-md-9">

                  <div
                    className="profileForm row"
                    style={{
                      textAlign:
                        "left",
                    }}
                  >

                    <div className="form-group mb-2 col-sm-6">

                      <label
                        style={{
                          fontSize:
                            "12px",
                          color:
                            "#777",
                        }}
                      >
                        Name
                      </label>

                      <input
                        type="text"
                        name="name"
                        className="form-control py-2"
                        value={
                          formData.name ||
                          ""
                        }
                        onChange={
                          handleChange
                        }
                        onBlur={
                          handleNameBlur
                        }
                      />

                      {errors.name && (
                        <small className="text-danger">
                          {
                            errors.name
                          }
                        </small>
                      )}

                    </div>

                    <div className="form-group mb-2 col-sm-6">

                      <label
                        style={{
                          fontSize:
                            "12px",
                          color:
                            "#777",
                        }}
                      >
                        Email Address
                      </label>

                      <input
                        type="email"
                        name="email"
                        className="form-control py-2"
                        value={
                          formData.email ||
                          ""
                        }
                        onChange={
                          handleChange
                        }
                        onBlur={
                          handleEmailBlur
                        }
                      />

                      {errors.email && (
                        <small className="text-danger">
                          {
                            errors.email
                          }
                        </small>
                      )}

                    </div>

                    <div className="form-group mb-2 col-sm-12">

                      <label
                        style={{
                          fontSize:
                            "12px",
                          color:
                            "#777",
                        }}
                      >
                        Mobile No.
                      </label>

                      <input
                        type="tel"
                        name="mobile"
                        className="form-control py-2"
                        value={
                          formData.mobile ||
                          ""
                        }
                        maxLength={10}
                        onChange={
                          handleChange
                        }
                        onBlur={
                          handleMobileBlur
                        }
                      />

                      {errors.mobile && (
                        <small className="text-danger">
                          {
                            errors.mobile
                          }
                        </small>
                      )}

                    </div>

                    <div className="form-group mb-2 col-sm-12">

                      <label
                        style={{
                          fontSize:
                            "12px",
                          color:
                            "#777",
                        }}
                      >
                        Wallet Address
                      </label>

                      <input
                        type="text"
                        name="wallet_address"
                        className="form-control py-2"
                        value={
                          formData.wallet_address ||
                          ""
                        }
                        onChange={
                          handleChange
                        }
                        readOnly={
                          !isEditing
                        }
                      />

                      {errors.wallet_address && (
                        <small className="text-danger">
                          {
                            errors.wallet_address
                          }
                        </small>
                      )}

                    </div>

                    <div className="mb-2 col-sm-12">

                      <button
                        onClick={
                          saveProfile
                        }
                        type="button"
                        disabled={
                          saving ||
                          pageLoading
                        }
                        className="text-center mybtn btn-primary d-flex gap-2 align-items-center w-100 mt-2"
                      >
                        <span className="bgIN">
                          {saving
                            ? "Saving..."
                            : "Save Profile"}
                        </span>
                      </button>

                      <p className="text-center mt-3">

                        Want to Edit{" "}

                        <Link
                          className="golderText"
                          to="/co-passenger-profile"
                        >
                          Create Co-Passenger Profile?
                        </Link>

                      </p>

                    </div>

                  </div>
                </div>
              </div>

            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default ProfilePage;